/**
 * Krok 2: O Twojej hodowli (opis + asystent „napisz za mnie")
 * Asystent korzysta z generowania w tle — w UI NIE pokazujemy nazwy „AI".
 */
import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Loader2, PawPrint, Wand2, Zap, ShoppingBag, Scissors, Check,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';

const HELPERS = [
  { id: 'improve', label: 'Popraw', icon: Wand2 },
  { id: 'professional', label: 'Profesjonalny ton', icon: Zap },
  { id: 'sales', label: 'Bardziej sprzedażowy', icon: ShoppingBag },
  { id: 'shorten', label: 'Skróć', icon: Scissors },
];

const MIN_LEN = 80;
const MAX_LEN = 600;

export default function Step2About({ data, updateData }) {
  const [busy, setBusy] = useState(null);
  const description = data.description || '';
  const len = description.length;
  const valid = len >= MIN_LEN;

  const callRefine = async (mode) => {
    if (!description.trim()) {
      toast.error('Najpierw napisz kilka słów');
      return;
    }
    setBusy(mode);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/ai/refine`,
        {
          text: description,
          mode,
          context: { kennel_name: data.name, breeds: data.breeds, city: data.city },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const txt = res.data?.content;
      if (txt) {
        updateData({ description: txt });
        toast.success('Tekst ulepszony!');
      } else {
        toast.error('Nie udało się ulepszyć tekstu');
      }
    } catch {
      toast.error('Asystent chwilowo niedostępny');
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (!data.name || !(data.breeds || []).length) {
      toast.error('Najpierw uzupełnij krok 1 (nazwa + rasa)');
      return;
    }
    setBusy('generate');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/ai/generate`,
        {
          type: 'kennel_description',
          context: { kennel_name: data.name, breeds: data.breeds, city: data.city },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.content) {
        updateData({ description: res.data.content });
        toast.success('Gotowe — propozycja opisu wstawiona');
      }
    } catch {
      toast.error('Nie udało się napisać opisu');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="wizard-v2-step-about">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <PawPrint className="w-5 h-5 text-orange-500" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
            O Twojej hodowli
          </h2>
        </div>
        <p className="text-gray-500 text-sm">
          Opowiedz o sobie — co Cię wyróżnia, jak długo prowadzisz hodowlę, co jest dla Ciebie ważne.
        </p>
      </header>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-gray-900">
            Opowiedz kilka słów o swojej hodowli
          </Label>
          <span className={`text-xs font-medium ${valid ? 'text-emerald-600' : 'text-gray-400'}`}>
            {len}/{MAX_LEN} {valid && <Check className="inline w-3.5 h-3.5 ml-0.5" />}
          </span>
        </div>
        <Textarea
          value={description}
          onChange={(e) => updateData({ description: e.target.value })}
          placeholder="np. Od 10 lat hodujemy zdrowe i zrównoważone psy. Specjalizujemy się w rasie X. Nasze psy żyją z nami w domu, otoczone ludźmi i codziennymi dźwiękami…"
          rows={8}
          maxLength={MAX_LEN}
          data-testid="wizard-v2-description-textarea"
          className="text-base resize-none focus-visible:ring-orange-400"
        />
        <p className="text-xs text-gray-500">
          {len < MIN_LEN
            ? `Dodaj jeszcze ${MIN_LEN - len} znaków dla lepszego efektu`
            : 'Świetnie! Klienci pokochają ten opis.'}
        </p>
      </section>

      {/* Asystent pisania (bez nazwy „AI") */}
      <section className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50/60 to-orange-50/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 className="w-4 h-4 text-orange-500" />
          <p className="text-sm font-semibold text-orange-900">Pomożemy Ci napisać</p>
        </div>

        {description.trim().length === 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={generate}
            disabled={busy !== null}
            data-testid="wizard-v2-ai-generate-btn"
            className="w-full bg-white border-orange-200 hover:bg-orange-50"
          >
            {busy === 'generate' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2 text-orange-500" />}
            Napisz za mnie
          </Button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {HELPERS.map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => callRefine(id)}
                disabled={busy !== null}
                data-testid={`wizard-v2-ai-helper-${id}`}
                className="bg-white border-gray-200 hover:bg-orange-50 hover:border-orange-300 justify-start"
              >
                {busy === id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Icon className="w-3.5 h-3.5 mr-1.5 text-orange-500" />}
                <span className="text-xs">{label}</span>
              </Button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
